<template>
  <b-section-loader :loading="$loading.isLoading('initDocumentTemplateGroups')">
    <div>
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="label mb-0">Етапи маршруту</div>

        <v-btn @click="addGroup()">
          <v-icon class="mr-1" icon="mdi-plus"></v-icon>
          Додати групу
        </v-btn>
      </div>

      <div v-if="!groupList.length" class="text-center border rounded-lg pa-4">
        <span>Для створення маршрута документа додайте хоча б одну групу</span>
      </div>

      <template v-else>
        <route-steps
          :active-group-id="currentGroup?.id || null"
          :queues="sortedGroupList"
          @add-group="addGroup"
          @open-group="openGroup">
        </route-steps>

        <route-group-form
          v-if="currentGroup"
          :key="currentGroup.id"
          :group="currentGroup"
          :queue-length="sortedGroupList.length"
          :queue-number="currentQueueNumber"
          :parallel-groups-count="activeQueueGroups.length"
          :source="source"
          :used-user-ids="usedUserIds"
          class="mt-6"
          @add-to-stage="addGroup(currentQueueNumber)"
          @decrement="changeQueueNumber('minus')"
          @delete-group="deleteGroup"
          @detach-parallel="detachParallelGroup"
          @increment="changeQueueNumber('plus')"
          @update:group="updateGroup(currentGroup.id, $event)">
        </route-group-form>
      </template>
    </div>
  </b-section-loader>
</template>

<script setup lang="ts">
import RouteSteps from './components/RouteSteps.vue'
import RouteGroupForm from './components/RouteGroupForm.vue'
import { computed, reactive, ref, shallowReactive } from 'vue'
import { cachedObject } from 'best-modules/utils'
import { BSectionLoader } from 'best-modules/components'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { fillDirectory } from '@/directory'
import {
  TemplateRoute,
  TemplateRouteSource,
  templateRouteCreate,
  templateRouteDelete,
  templateRouteList,
  templateRouteUpdate,
} from '@/requests/templateRoute'

defineOptions({
  name: 'TemplateGroup',
})

const props = defineProps<{
  dialog: Record<string, any>
}>()

const source = ref<TemplateRouteSource>(
  props.dialog.dialogData.document ? 'agreement' : 'default'
)

const groupList = shallowReactive<TemplateRoute[]>([])

const currentState = reactive<{
  queueNumber: number | null
  groupId: number | null
}>({
  queueNumber: null,
  groupId: null,
})

const currentGroup = computed(() => {
  if (!currentState.groupId) return null
  return groupList.find(group => group.id === currentState.groupId) || null
})

const currentQueueNumber = computed(() => {
  return currentState.queueNumber ?? sortedGroupList.value[0]?.queueNumber ?? 1
})

const activeQueueGroups = computed(() => {
  return (
    sortedGroupList.value.find(
      queue => queue.queueNumber === currentQueueNumber.value
    )?.groups || []
  )
})

const usedUserIds = computed(() => {
  return groupList.flatMap(group => {
    return (group.users || []).map(user => user.userId)
  })
})

const sortedGroupList = computed(() => {
  const map = new Map<
    number,
    {
      queueNumber: number
      groups: TemplateRoute[]
    }
  >()

  for (const group of groupList) {
    if (!map.has(group.queueNumber)) {
      map.set(group.queueNumber, {
        queueNumber: group.queueNumber,
        groups: [],
      })
    }

    map.get(group.queueNumber).groups.push(group)
  }

  return Array.from(map.values()).sort(
    (first, second) => first.queueNumber - second.queueNumber
  )
})

const setCurrent = (queueNumber?: number | null, group?: TemplateRoute) => {
  if (!groupList.length) {
    currentState.queueNumber = null
    currentState.groupId = null
    return
  }

  if (group) {
    currentState.queueNumber = queueNumber ?? group.queueNumber
    currentState.groupId = group.id
    return
  }

  const firstQueue = sortedGroupList.value[0]
  const firstGroup = firstQueue?.groups?.[0]

  if (firstGroup) {
    currentState.queueNumber = firstQueue.queueNumber
    currentState.groupId = firstGroup.id
  }
}

const openGroup = (queueNumber?: number, group?: TemplateRoute) => {
  setCurrent(queueNumber, group)
}

const updateGroup = (groupId: number, updatedData: TemplateRoute) => {
  const group = groupList.find(item => item.id === groupId)
  if (!group) return

  Object.assign(group, updatedData)
}

const pushGroup = (raw: TemplateRoute) => {
  const group = cachedObject(raw)
  group.$setIgnore?.('users')
  groupList.push(group)
  return group
}

const changeQueueNumber = (action: 'minus' | 'plus') => {
  const active = currentGroup.value
  if (!active) return

  const from = active.queueNumber
  const to = action === 'plus' ? from + 1 : from - 1

  const currentQueue = sortedGroupList.value.find(
    queue => queue.queueNumber === from
  )
  const neighborQueue = sortedGroupList.value.find(
    queue => queue.queueNumber === to
  )

  if (!currentQueue || !neighborQueue) return

  currentQueue.groups.forEach(group =>
    updateGroup(group.id, {
      ...group,
      queueNumber: to,
    })
  )
  neighborQueue.groups.forEach(group =>
    updateGroup(group.id, {
      ...group,
      queueNumber: from,
    })
  )

  setCurrent(to, active)
}

const detachParallelGroup = () => {
  const active = currentGroup.value
  if (!active || activeQueueGroups.value.length <= 1) return

  const from = active.queueNumber
  const to = from + 1

  groupList.forEach(group => {
    if (group.id === active.id) return

    if (group.queueNumber > from) {
      updateGroup(group.id, {
        ...group,
        queueNumber: group.queueNumber + 1,
      })
    }
  })

  updateGroup(active.id, {
    ...active,
    queueNumber: to,
  })
  setCurrent(to, active)
}

const hasGroupChanges = (group: TemplateRoute) => {
  return !!Object.keys(group.$getChangedFields()).length
}

const submit = async () => {
  const changedGroups = groupList
    .filter(hasGroupChanges)
    .sort((first, second) => second.queueNumber - first.queueNumber)

  for (const group of changedGroups) {
    const updatedGroup = await templateRouteUpdate(
      source.value,
      group.id,
      group.$object
    )
    group.$set(updatedGroup)
  }

  return getTemplateRouteSingle().then(res => {
    setSnackbar({ text: 'Дані збережено' })

    return res
  })
}

const getTemplateRouteSingle = () => {
  const id =
    props.dialog.dialogData.templateId || props.dialog.dialogData.document?.id

  return templateRouteList(source.value, id).then(res => {
    groupList.splice(0, groupList.length)

    if (source.value === 'agreement' && Array.isArray(res)) {
      res.forEach(pushGroup)
    } else if (!Array.isArray(res)) {
      res.routes.forEach(pushGroup)
    }

    openGroup()
    return res
  })
}

const getNextGroupName = () => {
  const usedNumbers = new Set(
    groupList
      .map(group => group.name.match(/^Група (\d+)$/)?.[1])
      .filter(Boolean)
      .map(Number)
  )

  let number = 1

  while (usedNumbers.has(number)) {
    number++
  }

  return `Група ${number}`
}

const addGroup = (queueNumber?: number) => {
  return templateRouteCreate(source.value, {
    name: getNextGroupName(),
    templateId: props.dialog.dialogData.templateId,
    documentAgreementId: props.dialog.dialogData.document?.id,
    queueNumber,
  }).then(group => {
    const createdGroup = pushGroup(group)
    openGroup(group.queueNumber, createdGroup)
    return group
  })
}

const deleteGroup = () => {
  const active = currentGroup.value
  if (!active) return Promise.resolve()

  return templateRouteDelete(source.value, active.id).then(res => {
    const deletingQueue = active.queueNumber
    const sameQueue = groupList.filter(
      group => group.queueNumber === deletingQueue
    )
    const hasParallelGroups = sameQueue.length > 1
    const index = groupList.findIndex(group => group.id === active.id)

    if (index >= 0) {
      groupList.splice(index, 1)
    }

    if (!hasParallelGroups) {
      for (const group of groupList) {
        if (group.queueNumber > deletingQueue) {
          updateGroup(group.id, {
            ...group,
            queueNumber: group.queueNumber - 1,
          })
        }
      }
    }

    const next =
      groupList.find(group => group.queueNumber === deletingQueue) ||
      groupList.find(group => group.queueNumber === deletingQueue - 1) ||
      groupList[0] ||
      null

    if (next) {
      setCurrent(next.queueNumber, next)
    } else {
      setCurrent(null, null)
    }

    return res
  })
}

const init = () => {
  return handleAsync('initDocumentTemplateGroups', () => {
    return Promise.all([
      getTemplateRouteSingle(),
      fillDirectory([
        'userList',
        'templateRouteActions',
        'templateRouteAgreements',
      ]),
    ])
  })
}

init()

defineExpose({
  submit,
})
</script>
